import {APP_INITIALIZER, enableProdMode, importProvidersFrom} from '@angular/core';

import {HttpLoaderFactory} from './app/app.module';
import {environment} from './environments/environment';
import {DatePipe} from '@angular/common';
import {ProductBatchesPipe} from './app/pipes/product-batches/product-batches.pipe';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {HeaderInterceptorService} from './app/services/header-interceptor/header-interceptor.service';
import {
  AppInfoModule,
  AuthModule,
  AuthorizationInterceptorService,
  NotAuthenticatedInterceptorService
} from '@colimo/colimo-ui-library';
import {ThemeService} from './app/services/theme/theme.service';
import {LocaleService} from './app/services/locale/locale.service';
import {ProjectService} from './app/services/project/project.service';
import {AppRoutingModule} from './app/app-routing.module';
import {bootstrapApplication, BrowserModule} from '@angular/platform-browser';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {provideAnimations} from '@angular/platform-browser/animations';
import {MatCardModule} from '@angular/material/card';
import {MatDividerModule} from '@angular/material/divider';
import {MatInputModule} from '@angular/material/input';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatSelectModule} from '@angular/material/select';
import {MatTableModule} from '@angular/material/table';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatMenuModule} from '@angular/material/menu';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatBadgeModule} from '@angular/material/badge';
import {MatTabsModule} from '@angular/material/tabs';
import {MatRippleModule} from '@angular/material/core';
import {MatDialogModule} from '@angular/material/dialog';
import {StoreModule} from '@ngrx/store';
import {reducers} from './app/reducers';
import {ClarityModule} from '@clr/angular';
import {MissingTranslationHandler, TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {SharedModule} from './app/shared/shared.module';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {LoginModule} from './app/pages/login/login.module';
import {provideCloudStorage} from '@colorcare/ngx-cloud-storage';
import {provideNgxUtils} from '@colorcare/ngx-utils';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatSortModule} from '@angular/material/sort';
import {MatLuxonDateModule} from '@angular/material-luxon-adapter';
import {CdkDrag, CdkDropList} from '@angular/cdk/drag-drop';
import {MatGridListModule} from '@angular/material/grid-list';
import {AppComponent} from './app/app.component';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import {ColimoMissingTranslateHandler} from "./app/shared/helpers/colimo-missing-translate-handler";

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(AppRoutingModule, BrowserModule, FormsModule, ReactiveFormsModule, HttpClientModule, MatCardModule, MatDividerModule, MatInputModule, MatSlideToggleModule, MatSelectModule, MatTableModule, MatButtonModule, MatIconModule, MatTooltipModule, MatButtonToggleModule, MatSidenavModule, MatMenuModule, MatExpansionModule, MatBadgeModule, MatTabsModule, MatRippleModule, MatDialogModule, StoreModule.forRoot(reducers, {
              runtimeChecks: {
                  strictStateImmutability: false,
                  strictActionImmutability: false,
              },
          }),
          ClarityModule,
          TranslateModule.forRoot({
              loader: {
                  provide: TranslateLoader,
                  useFactory: HttpLoaderFactory,
                  deps: [HttpClient],
              },
              missingTranslationHandler: { provide: MissingTranslationHandler, useClass: ColimoMissingTranslateHandler },
              useDefaultLang: false,
          }),
          SharedModule.forRoot(),
          BsDatepickerModule.forRoot(),
          LoginModule,
          AuthModule.forRoot({
              baseUrl: environment.apiBasePath,
              baseAuthPath: environment.baseAuthPath,
              minimalValidationTimeLeftInSeconds: environment.minimalValidationTimeLeftInSeconds,
              loginRoute: environment.loginRoute,
              routeAfterLogin: environment.routeAfterLogin,
              debug: false,
              colorCareRoute: environment.colorCareRoute,
              colorCareLoginUrl: environment.colorCareLoginUrl,
          }),
          AppInfoModule.forRoot({
              backendUrl: environment.apiBasePath + environment.apiUserPath + '/' + environment.backendInfo,
          }),
          MatCheckboxModule, MatPaginatorModule, MatSnackBarModule, MatSortModule, MatLuxonDateModule, CdkDropList, CdkDrag, MatGridListModule
        ),
        provideNgxUtils({
            i18n: {
                translationFolders: ["./assets/i18n/"], /*, "./assets/ngx-cloud-storage/i18n/" */
                languages: [
                    { id: "de", data: localeDe, defaults: { dateFormat: "medium" } }, //urlMatcher: new RegExp(/^\/de(\/?|\/.*|\?.+=.*)$/)
                    { id: "en", data: localeEn, defaults: { dateFormat: "medium" } }, //urlMatcher: new RegExp(/^\/en(\/?|\/.*|\?.+=.*)$/),
                ],
                defaults: {
                    language: "en",
                },
            },
        }),
        {
          provide: MissingTranslationHandler,
          useClass: ColimoMissingTranslateHandler,
        },
        provideCloudStorage({
          cloudStorageServiceUrl: environment.colorCareCloudStorageServiceUrl,
          userManagementServiceUrl: environment.colorCareEvoUserUrl,
          allowDownloadViaDirectDownloadLink: false,
        }),
        // expose our Services and Providers into Angular's dependency injection
        DatePipe,
        ProductBatchesPipe,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderInterceptorService,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthorizationInterceptorService,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NotAuthenticatedInterceptorService,
            multi: true,
        },
        ThemeService,
        ProjectService,
        {
            provide: APP_INITIALIZER,
            useFactory: (localeService: LocaleService) => () => localeService.defineUsedLocales(),
            deps: [LocaleService],
            multi: true,
        },
        provideAnimations(),
    ]
})
  .catch(err => console.error(err));
